import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { InputAppClientDBFacade } from '@monorepo/core-data';
import {
  BCTX_KEYS_TO_DISPLAY_MAP,
  BCTX_KEYS_TO_FILTER_TITLES,
  TBCTXKeysWithoutSize,
  TBctxItem,
  TBctxItemWithWipTransferDetails,
  TBctxItemWithoutSize,
  TBctxItemWithoutSizeAndBctxKey,
  TKeyDisplayMap,
} from '@monorepo/types';
import { BCTXComputationHelper, InputAppAPIHelperUtil } from '@monorepo/utils';
import { TranslateService } from '@ngx-translate/core';
import { AppMasterDBFacade } from 'libs/core-data/src/lib/input-app/redux-store/app-master-db/app-master-db.facade';

@Component({
  selector: 'monorepo-grouped-bctx-listing',
  templateUrl: './grouped-bctx-listing.component.html',
  styleUrls: ['./grouped-bctx-listing.component.scss'],
})
export class GroupedBctxListingComponent implements OnInit {
  @Input() header: string = '';
  @Input() buttonTxt = '';
  @Input() bctxKeysToDisplayMap = BCTX_KEYS_TO_DISPLAY_MAP;
  @Input() bctxKeysToFilterTitles = BCTX_KEYS_TO_FILTER_TITLES;
  @Input() selectedBctxItem: TBctxItemWithoutSizeAndBctxKey[] = [];
  @Input() excludeColorFilter = false;
  orderKeysToDisplay: TBCTXKeysWithoutSize[] = [];

  @Output() onClose = new EventEmitter<'CLOSE'>();
  @Output() onBctxSelection = new EventEmitter<TBctxItemWithWipTransferDetails>();

  factoryId = '';
  uniqueBctxList: TBctxItemWithoutSizeAndBctxKey[] = [];
  bctxList: TBctxItemWithoutSizeAndBctxKey[] = [];
  groupedHeaderInfo: TKeyDisplayMap[] = [];

  constructor(
    private appMasterDBFacade: AppMasterDBFacade,
    private apiHelper: InputAppAPIHelperUtil,
    public translateService: TranslateService,
    private inputAppClientDbFacde: InputAppClientDBFacade,
  ) {
    this.appMasterDBFacade.getSubjectKey().subscribe((res) => {
      this.factoryId = res;
    });
  }

  ngOnInit(): void {
    this.getBCtxGroupedDetails();
  }

  handleBctxSelection(event: TBctxItemWithoutSizeAndBctxKey) {
    this.onBctxSelection.emit({
      event,
      wipTransferDetails: {
        wipFrom: this.selectedBctxItem[0].list.reduce((acc: { [key: string]: string }, curr: TBctxItem) => {
          const groupedItem = this.groupedHeaderInfo.filter((item) => item.key === curr.k);
          if (groupedItem && groupedItem.length) {
            acc[curr.k] = curr.v;
          }
          return acc;
        }, {} as { [key: string]: string }),
        wipTo: event.list.reduce((acc: { [key: string]: string }, curr: TBctxItem) => {
          const groupedItem = this.groupedHeaderInfo.filter((item) => item.key === curr.k);
          if (groupedItem && groupedItem.length) {
            acc[curr.k] = curr.v;
          }
          return acc;
        }, {} as { [key: string]: string }),
        groupedHeaderInfo: this.groupedHeaderInfo,
      },
    });
  }

  handleClose() {
    this.onClose.emit('CLOSE');
  }

  handleSync() {
    this.getBCtxGroupedDetails();
  }

  getBCtxGroupedDetails() {
    this.apiHelper.presentLoading('Loading..');
    let bctxList: TBctxItemWithoutSizeAndBctxKey[] = [];
    // ZSR-1449: When excludeColorFilter is true (packing PO split scenarios), omit 'color'
    // from the BCTX filter payload so all daughter POs appear regardless of color.
    // Size is structurally absent from this payload — item.k is TBCTXKeysWithoutSize which excludes 'size'.
    this.inputAppClientDbFacde
      .getBCtxGroupedDetails({
        subjectKey: this.factoryId,
        bctxItemList: this.selectedBctxItem[0].list
          .filter((item) => !this.excludeColorFilter || item.k !== 'color')
          .map((item) => ({
            k: item.k,
            v: item.v,
          })),
      })
      .subscribe(
        (res) => {
          this.apiHelper.dismissLoading();
          if (res.responseStatus === 'SUCCESS') {
            this.groupedHeaderInfo = res.response.groupedHeaderInfo;
            res.response.bctxGroupInfoWithCommonInfo.bctxGroupInfo.bctxGroupItemList.forEach((item) => {
              let list: TBctxItemWithoutSize[] = [];
              const keys = res.response.commonHeaderInfo.map((item) => {
                return item.key;
              });
              res.response.groupedHeaderInfo.forEach((item) => {
                keys.push(item.key);
              });
              const orderKeys = this.filterToTBCTXKeysWithoutSize(keys);
              orderKeys.forEach((key) => {
                if (res.response.bctxGroupInfoWithCommonInfo.bctxGroupCommonInfo[key]) {
                  list.push({ k: key, v: res.response.bctxGroupInfoWithCommonInfo.bctxGroupCommonInfo[key] });
                } else {
                  let groupingItem = item.bctxItemList.filter((bctxItem) => bctxItem.k === key);
                  if (groupingItem && groupingItem.length) {
                    list.push(groupingItem[0]);
                  }
                }
              });
              let bctx_key = BCTXComputationHelper.getUniqueBctxKeyWithOrder(orderKeys, list);
              bctxList.push({
                bctxKey: bctx_key,
                list: list,
              });
            });
            this.orderKeysToDisplay = this.filterToTBCTXKeysWithoutSize(
              res.response.groupedHeaderInfo.map((item) => item.key),
            );
          } else {
            this.apiHelper.showPopup('Error', res.error ? res.error : 'Something Went Wrong');
          }
          this.bctxList = bctxList;
        },
        (err) => {
          this.apiHelper.dismissLoading();
          this.apiHelper.showPopup('Error', 'Something Went Wrong');
        },
      );
  }

  filterToTBCTXKeysWithoutSize = (arr: string[]): TBCTXKeysWithoutSize[] => {
    const validKeys: TBCTXKeysWithoutSize[] = [
      'buyer',
      'style',
      'po',
      'color',
      'product_type',
      'product_category',
      'order_ref',
      'search_id',
      'audit_disName',
      'bundle_no',
      'display_piece_tag',
    ];
    return arr.filter((key): key is TBCTXKeysWithoutSize => validKeys.includes(key as TBCTXKeysWithoutSize));
  };
}
